import styled from '@emotion/styled';
import { t } from '@lingui/macro';
import { Box, type BoxProps } from '@mui/material';
import { CloudArrowUp } from '@phosphor-icons/react';

import React from 'react';
import { type DropzoneOptions, useDropzone } from 'react-dropzone';
import { Text } from 'components/text/Text';
import { Button } from 'components/button/Button';

type FileUploadProps = {
  supportedFiles?: React.ReactNode;
  supportedFilesTitle?: React.ReactNode;
  dragActiveTitle?: React.ReactNode;
  dragTitle?: React.ReactNode;
  browseTitle?: React.ReactNode;
  dropzoneOptions?: DropzoneOptions;
  rootProps?: BoxProps;
  hideBrowse?: boolean;
  name?: string;
  value?: File[];
  helperText?: React.ReactNode;
};

export const FileUpload = ({
  supportedFiles = '.csv, .xls, .xlsx',
  dragActiveTitle = t`Drop your file here...`,
  browseTitle = t`browse`,
  dragTitle = t`Drop your file or`,
  supportedFilesTitle = t`Files supported`,
  dropzoneOptions,
  rootProps,
  hideBrowse,
  ...props
}: FileUploadProps) => {
  const onDrop: DropzoneOptions['onDrop'] = (acceptedFiles: File[], fileRejections, event) => {
    dropzoneOptions?.onDrop?.(acceptedFiles, fileRejections, event);
  };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    ...dropzoneOptions,
    onDrop,
  });

  return (
    <StyledBox {...getRootProps<Omit<BoxProps, 'color'>>({ ...(rootProps ?? {}) })}>
      {isDragActive ? (
        <Text
          variant="bodySm"
          weight="regular"
          color="secondary"
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px' }}
        >
          {dragActiveTitle}
        </Text>
      ) : (
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CloudArrowUp size={20} />
          <Text
            variant="bodySm"
            weight="regular"
            color="secondary"
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px' }}
          >
            {dragTitle}{' '}
            {!hideBrowse && (
              <Button variant="link" size="sm" component="label">
                {browseTitle}
                <input {...getInputProps()} />
              </Button>
            )}
          </Text>
        </Box>
      )}
      <Text variant="bodyXs" weight="regular" color="quaternary">
        {supportedFilesTitle}: {supportedFiles}
      </Text>
    </StyledBox>
  );
};

const StyledBox = styled(Box)`
  display: flex;
  padding: 24px 0px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex: 1 0 0;
  align-self: stretch;
  border-radius: 8px;
  border-style: dashed;
  border-width: 1px;
  border-color: ${p => p.theme.palette.border.primary};
  background: ${p => p.theme.palette.bg.primary};
`;
